<template>
  <div class="scenario-details-card" :class="{ editing: editMode, fullscreen: fullscreen }">
    <div v-if="editingCopyrightCrud" class="backdrop-layer"></div>
    <!-- Modals -->
    <ScenarioEditConditionsDialog v-model="selectedConditions" :enableWhen.sync="selectedEnableWhen"
      :enableBehavior.sync="selectedEnableBehavior" :dialog="modals.conditions.open"
      :representingTemplate="itemsRepresentingTemplate" @close="stopEditConditions" textual structural />

    <EditableRichText :showDialog="modals.context.open" :mode="selectedContext ? 'edit' : 'add'"
      @cancel="stopEditContext" v-model="selectedContext" allowEmptyText />

    <AdArtefactSelection
      v-if="modals.artefactSelection.tree"
      v-model="modals.artefactSelection.open"
      :tree="modals.artefactSelection.tree"
      :exclude="modals.artefactSelection.selected"
      :versionProperty="modals.artefactSelection.versionProp"
      :treeFilters="modals.artefactSelection.filters"
      noDynamicVersions
      @selected="modals.artefactSelection.selectHandler"
      @close="resetArtefactSelectionModal"
    >
      <template  #actions="{close, loading, save, selectedVersion, selectedVersionIsDisabled, selectionBasketContainsVersion}">
        <v-spacer/>
        <AdButton type="cancel" @click="close"/>
        <AdButton
          v-if="showSmartCloneSourceDatasetOptions"
          type="preflight"
          :text="$t('smart-cloning-preflight')"
          :disabled="loading || !selectedVersion || selectedVersionIsDisabled || selectionBasketContainsVersion"
          @click="startSmartClonePreflight(selectedVersion)"
        />
        <AdButton :type="artefactSelectionAddButtonType" @click="save" :disabled="loading || !selectedVersion || selectedVersionIsDisabled || selectionBasketContainsVersion"/>
      </template>

      <template v-if="showSmartCloneSourceDatasetOptions" #details.content:append="{ selectedVersion }">
        <v-card>
          <AdCardTitle inline>{{ $t('reassign') }} / {{ $t('smart-cloning') }}</AdCardTitle>
          <v-card-text>
            <AdInfoOwl>{{ $t('smart-cloning-hint') }}</AdInfoOwl>
            <table>
              <tr v-if="sourceDatasetInfo.id">
                <td class="py-3 pr-3">{{ $t('original-source-dataset') }}</td>
                <td>
                  <v-chip :color="$filters.getArtefactColor('DS')">
                    <div>
                      <AdStatusDot larger tooltip :status="sourceDatasetInfo.statusCode" class="mr-2"/>
                      {{ $filters.getMatchingTranslation({ value: sourceDatasetInfo.name }) }}
                      <span v-if="sourceDatasetInfo.versionLabel">({{ sourceDatasetInfo.versionLabel }})</span>
                      {{ $t('as-of') }}
                      {{ $filters.formatDateShort(sourceDatasetInfo.effectiveDate) }}
                    </div>
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="pr-3">{{ $t('new-source-dataset') }}</td>
                <td>
                  <v-chip :color="$filters.getArtefactColor('DS')">
                    <div>
                      <AdStatusDot larger tooltip :status="selectedVersion.statusCode" class="mr-2"/>
                      {{ $filters.getMatchingTranslation({ value: selectedVersion.name }) }}
                      <span v-if="selectedVersion.versionLabel">({{ selectedVersion.versionLabel }})</span>
                      {{ $t('as-of') }}
                      {{ $filters.formatDateShort(selectedVersion.effectiveDate) }}
                    </div>
                  </v-chip>
                </td>
              </tr>
            </table>

            <div class="pt-5" v-html="$t('smart-cloning-reassign-intention-hint')"/>
            <div class="pt-3 pb-5" v-html="$t('smart-cloning-intention-hint')"/>

            <AdInfoOwl warning>{{ $t('actions-cannot-be-made-undone') }}</AdInfoOwl>
          </v-card-text>
        </v-card>
      </template>
    </AdArtefactSelection>

    <SmartCloneDialog v-model="modals.smartCloneDialog.open" :transaction="modals.smartCloneDialog.transaction" :dataset="modals.smartCloneDialog.newDataset" @clone="handleSmartClone"/>

    <ScenarioSimplifiedSpreadsheetOrGraphDialog
      v-model="modals.spreadsheetOrGraph.open"
      :dialog="modals.spreadsheetOrGraph.open"
      :stype="modals.spreadsheetOrGraph.graph ? 'graph' : (modals.spreadsheetOrGraph.spreadsheet ? 'table' : '')"
      :representingTemplate="itemsRepresentingTemplate" @close="stopSimplifiedSpreadsheetOrGraph" />

    <v-card v-if="scenarioItem">
      <div v-if="!!editMode" class="backdrop-layer"></div>
      <div class="scenario-details-card-header d-flex align-center flex-row backdrop-item">
        <v-slide-y-transition group leave-absolute>
          <AdCardTitle key="title" class="flex-grow-1">
            <div key="scenarioItemName">
              <AdStatusDot darker tooltip larger :status="scenarioItem.statusCode"
                :icon="$filters.getScenarioIcon(scenarioItem.type)" class="mr-2 mb-1" />
              {{ scenarioName }}
            </div>

          </AdCardTitle>
          <v-card-subtitle v-if="isTransactionLeaf && sourceDatasetInfo.name" key="subtitle" class="subtitle-2 pt-2">
            {{ $t('source-dataset') }}: {{ $filters.getMatchingTranslation({ value: sourceDatasetInfo.name }) }}
          </v-card-subtitle>
        </v-slide-y-transition>

        <v-spacer />

        <template v-if="!!scenarioItem.representingTemplate">
          <AdArtefactDownloads v-model="modals.artefactDownload" :title="`${$t('downloads')} - ${scenarioName}`"
            :downloadData="scenarioDownloadData" />
          <AdButton type="downloads" @click="modals.artefactDownload = true" />
        </template>

        <IssueCreationModal :object="scenarioItem" @create="handleCreateIssue" />
        <!-- compare button, for transaction leafs only, DISABLED BY NOW -->
        <ScenarioCompareDialog v-show="$feature('showDevelopmentFeature3')"
          v-if="false && isTransactionLeaf && itemsRepresentingTemplate !== null && !editMode"
          ref="scenarioCompareDialog" :scenario1="itemsRepresentingTemplate" :scenario2="itemsRepresentingTemplate" />
      </div>

      <v-card-text>
        <EditableArtefactDetailHeader :artefact="editableItem.type != null ? 'Transaction' : 'Scenario'"
          :item="editableItem" :locales="projectLocales" :readonly="!isEditable" :showMore.sync="showMoreMetaData"
          :is-editable-status-code="userIsEditor" :service="patchItem">
          <div v-show="!fullscreen" class="mb-5">
            <v-row dense>
              <v-col cols="4" v-if="isTransactionLeaf">
                <EditableDropdown v-model="editableItem.type" :locales="projectLocales"
                  :prependIcon="$filters.getScenarioIcon(editableItem.type)" :items="transactionLeafTypes"
                  item-key="value" item-text="label" :label="$t('type')"
                  @change="saveMetaInformationValue('type', $event)" :readonly="!isEditable" />
              </v-col>

              <v-col cols="4" v-if="isTransactionLeaf">
                <EditableString v-model="editableItem.model" :locales="projectLocales" :label="$t('model')"
                  :readonly="!isEditable" confirmation-required @change="saveMetaInformationValue('model', $event)" />
              </v-col>
              <v-col cols="4" v-if="isTransactionLeaf">
                <EditableString v-model="editableItem.label" :locales="projectLocales" :label="$t('label')"
                  :readonly="!isEditable" confirmation-required @change="saveMetaInformationValue('label', $event)" />
              </v-col>
              <v-col cols="4">
                <EditableString v-model="editableItem.canonicalUri" :label="$t('canonicalUri')"
                  @change="saveMetaInformationValue('canonicalUri', $event)" show-delete-icon confirmation-required
                  :readonly="!isEditable" />
              </v-col>
              <v-col cols="4">
                <EditableDateTime v-model="editableItem.officialReleaseDate" :locales="projectLocales"
                  :label="$t('official-release-date')" :readonly="!isEditable" confirmation-required
                  @change="saveMetaInformationValue('officialReleaseDate', $event)" />
              </v-col>
              <v-col cols="4">
                <EditableDateTime v-model="editableItem.expirationDate" :locales="projectLocales"
                  :label="$t('expiration-date')" :readonly="!isEditable" confirmation-required
                  @change="saveMetaInformationValue('expirationDate', $event)" />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-2">
                <div v-if="!contains(editableItem,'condition')" class="d-flex align-center">
                  <span class="valueDomainLabel">{{ $t('condition') }}</span>
                  <AdButton v-if="isEditable" type="add" small list @click="addMetaItem('condition')" />
                </div>
                <div v-else>
                  <EditableTranslationEditor v-model="editableItem.condition" :locales="projectLocales"
                    :label="$t('condition')" :readonly="!isEditable" confirmation-required
                    @change="saveMetaInformationValue('condition', $event)" editor />
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-2">
                <div v-if="!contains(editableItem,'trigger')" class="d-flex align-center">
                  <span class="valueDomainLabel">{{ $t('trigger') }}</span>
                  <AdButton v-if="isEditable" type="add" small list @click="addMetaItem('trigger')" />
                </div>
                <div v-else>
                  <EditableTranslationEditor v-model="editableItem.trigger" :locales="projectLocales"
                    :label="$t('trigger')" :readonly="!isEditable" confirmation-required
                    @change="saveMetaInformationValue('trigger', $event)" editor />
                </div>
              </v-col>
            </v-row>
            <!--
              MISSING FIELDS: trigger, condition, dependencies
              here is the start
            <v-row dense>
              <v-col cols="11" class="py-2">
                <div v-if="scenarioItem.trigger === undefined">
                  <span class="valueDomainLabel">{{ $t("trigger") }}</span>
                  <AdButton
                    type="add"
                    small
                    list
                    @click="addMetaItem('trigger', [])"
                  />
                </div>
                <div v-else>
                  <EditableTranslationEditor
                    v-model="scenarioItem.trigger"
                    :label="$t('trigger')"
                    dense
                  />
                </div>
              </v-col>
            </v-row>
            -->

            <!-- Source Dataset -->
            <v-row v-if="isTransactionLeaf" dense class="py-2">
              <v-col cols="3">
                <AdButton v-if="isEditable" :type="sourceDatasetInfo.id ? 'edit' : 'add'" :text="$t('source-dataset')"
                  @click="startSelectSourceDataset" :disabled="loading.sourceDataset" />
                <div v-else class="valueDomainLabel">{{ $t('source-dataset') }}</div>
              </v-col>
              <v-col cols="9" class="d-flex align-center">
                <v-progress-linear v-if="loading.sourceDataset" color="primary" indeterminate rounded
                  style="max-width: 300px" />
                <AdArtefactDetailsCard v-else-if="sourceDatasetInfo.id" :object="sourceDatasetInfo">
                  <template #activator.append>
                    <AdButton v-if="isEditable" type="erase" small list noMarginRight
                      @click.stop="saveSourceDatasetInfo(null)" />
                  </template>
                </AdArtefactDetailsCard>
              </v-col>
            </v-row>

            <!-- Representing Template -->
            <v-row v-if="isTransactionLeaf" dense class="pb-2">
              <v-col cols="3">
                <AdButton v-if="isEditable" :type="representingTemplateInfo.id ? 'edit' : 'add'" :text="$t('template')"
                  @click="startSelectRepresentingTemplate" :disabled="loading.representingTemplate" />
                <div v-else class="valueDomainLabel">{{ $t('template') }}</div>
              </v-col>
              <v-col cols="9" class="d-flex align-center">
                <v-progress-linear v-if="loading.representingTemplate" color="primary" indeterminate rounded
                  style="max-width: 300px" />
                <AdArtefactDetailsCard v-else-if="representingTemplateInfo.id" :object="representingTemplateInfo">
                  <template #activator.append>
                    <AdButton v-if="isEditable" type="erase" small list noMarginRight
                      @click.stop="saveRepresentingTemplateInfo(null)" />
                  </template>
                </AdArtefactDetailsCard>
              </v-col>
            </v-row>

            <!-- Questionnaire reference -->
            <v-row v-if="isTransactionLeaf" dense class="pb-3">
              <v-col cols="3">
                <AdButton v-if="isEditable" :type="representingTemplateQuestionnaireInfo.id ? 'edit' : 'add'"
                  :text="$t('questionnaire')" @click="startSelectRepresentingQuestionnaire"
                  :disabled="loading.representingQuestionnaire" />
                <div v-else class="valueDomainLabel">{{ $t('questionnaire') }}</div>
              </v-col>
              <v-col cols="9" class="d-flex align-center">
                <v-progress-linear v-if="loading.representingQuestionnaire" color="primary" indeterminate rounded
                  style="max-width: 300px" />
                <AdArtefactDetailsCard v-else-if="representingTemplateQuestionnaireInfo.id"
                  :object="representingTemplateQuestionnaireInfo">
                  <template #activator.append>
                    <AdButton v-if="isEditable" type="erase" small list noMarginRight
                      @click.stop="saveRepresentingTemplateQuestionnaireInfo(null)" />
                  </template>
                </AdArtefactDetailsCard>
              </v-col>
            </v-row>

            <v-row v-if="isTransactionLeaf" dense>
              <v-col cols="12">
                <v-card>
                  <AdCardTitle inline>
                    <div>{{ $t('actors') }}</div>
                    <v-spacer />
                    <ScenarioActorDialog v-if="isEditable" ref="actorDialog" :actor-list="actorList"
                      :existing-actor-list="transactionActorList" @change="saveActorList" />
                  </AdCardTitle>
                  <v-card-text>
                    <v-data-table :headers="actorHeaders" :items="transactionActorList" item-key="id"
                      :no-data-text="$t('no-actors')" hide-default-footer single-select dense>
                      <template v-slot:[`item.type`]="{ item }">
                        <v-icon v-if="item.type === 'organization'">mdi-hospital-building
                        </v-icon>
                        <v-icon v-if="item.type === 'person'">mdi-doctor</v-icon>
                        <v-icon v-if="item.type === 'device'">mdi-monitor</v-icon>
                      </template>

                      <template v-slot:[`item.name`]="{ item }">
                        {{ $filters.getMatchingTranslation({ value: item.name }) }}
                      </template>

                      <template v-slot:[`item.role`]="{ item }">
                        {{
                        $filters.getMatchingTranslation({
                        value: getMatchingActorTypeLabel(item.role),
                        locale: $i18n.locale
                        })
                        }}
                      </template>

                      <template v-slot:[`item.desc`]="{ item }">
                        <div v-html="$filters.getMatchingTranslation({ value: item.desc })" />
                      </template>

                      <template v-slot:[`item.data-actions`]="{ item }">
                        <div v-if="isEditable" class="text-no-wrap">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on: tooltip }">
                              <AdButton type="edit" list v-on="tooltip" class="mr-2"
                                @click="$refs.actorDialog.edit(item)" />
                            </template>
                            <span>{{ $t('edit') }}</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on: tooltip }">
                              <AdButton type="delete" list v-on="tooltip" class="mr-2"
                                @click="saveActorList({mode: 'delete', options: {id: item.id }})" />
                            </template>
                            <span>{{ $t('delete') }}</span>
                          </v-tooltip>
                        </div>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

              <v-row>
                <v-col>
                  <AdCopyrightCrud
                    v-model="editableItem"
                    ref="copyrightCrud"
                    :style="{ position: 'relative', zIndex: editingCopyrightCrud ? 3 : 1}"
                    :showRouterlinkToPublishingAuthority="false"
                    :enableAddCopyright="userIsEditor"
                    :enableEditCopyright="userIsEditor"
                    :enableRemoveCopyright="userIsEditor"
                    :enableAddPublishingAuthority="userIsEditor"
                    :enableEditPublishingAuthority="userIsEditor"
                    :enableRemovePublishingAuthority="userIsEditor"
                    :readonly="!editingCopyrightCrud || loading.copyrightCrud"
                    :loading="loading.copyrightCrud"
                    class="backdrop-item"
                  >
                    <template #header>
                      <AdCardTitle inline>
                        {{ $t('copyright-and-authorities') }}
                        <v-spacer/>
                        <AdButton v-if="isEditable && !editingCopyrightCrud" type="edit" @click="editingCopyrightCrud = true"/>
                        <AdButton
                          v-if="editingCopyrightCrud"
                          type="cancel"
                          :disabled="loading.copyrightCrud"
                          @click="handleCancelCopyrightCrud"
                        />
                        <AdButton
                          v-if="editingCopyrightCrud & (copyrightChanged || publishingAuthorityChanged)"
                          type="save"
                          :disabled="loading.copyrightCrud"
                          @click="handleSaveCopyrightCrud"
                        />
                      </AdCardTitle>
                    </template>
                  </AdCopyrightCrud>
                </v-col>
              </v-row>
          </div>
        </EditableArtefactDetailHeader>

        <template v-if="isTransactionGroup">
          <v-row v-if="scenarioItem && actorList !== null">
            <AdCreateSvg :transaction="scenarioItem" :actors="actorList" />
          </v-row>
        </template>

        <template v-else-if="isTransactionLeaf">
          <v-card class="backdrop-item card-backdrop">
            <v-card-text>
              <TreeTable v-model="activeConceptItem" ref="treeTable" class="tree-table"
                :select-mode="!editMode" :table-height="tableHeight" :table-height-auto-grow="tableAutoGrowOffsetHeight" fixed-header
                :headers="!editMode ? headersRepresentingTemplateRead : headersRepresentingTemplateEdit"
                :items="itemsRepresentingTemplate" :item-visible="isItemVisible" item-key="id" item-children="concept"
                :loading="isLoadingTransactionItem">
                <template v-slot:buttons>
                  <!--
                    AdSpeedDial with simplified graph or spreadsheet
                    not yet enabled KH 20231005 for release 3.7.1
                    in testing mode KH 20240226 for release 3.8.3
                  -->
                  <AdSpeedDial
                    v-if="false && !isLoadingTransactionItem && !editMode && (!editMode && !!itemsRepresentingTemplate.length)"
                    icon="mdi-eye" direction="bottom" color="green" class="pr-3" dropdown>
                    <AdSpeedOption class="pl-3" type="graph" :hint="$t('show-simplified-graph')"
                      @click="startSimplifiedGraph()" />
                    <AdSpeedOption type="table" :hint="$t('show-simplified-spreadsheet')"
                      @click="startSimplifiedSpreadsheet()" />
                  </AdSpeedDial>
                  <div
                    v-if="serverFeature({ path: 'decor.scenarios.transaction.representingTemplate', crudAction: 'write' })">
                    <AdButton v-if="isEditable && !editMode" :disabled="isLoadingTransactionItem" type="edit"
                      @click="startEditMode()" />
                    <AdButton v-if="isEditable && editMode" :disabled="isLoadingTransactionItem" type="save"
                      @click="saveEditedValues()" />
                    <AdButton v-if="isEditable && editMode" type="cancel" @click="cancelEditMode()" />
                    <AdButton v-if="!fullscreen" type="fullscreen" list @click="fullscreen = true" />
                    <AdButton v-if="fullscreen" type="exit-fullscreen" list @click="fullscreen = false" />
                  </div>
                </template>

                <template v-slot:active-item="{ item }">
                  <div v-if="!item">
                    <AdInfoOwl>{{ $t('select-concept') }}</AdInfoOwl>
                  </div>
                  <div v-else>
                    <!-- there is an item show dataset concept information and optional condition -->
                    <!-- dataset information -->
                    <AdArtefactDetailsCard v-if="item.id" inline :object="getArtefactRefObjectInfo(item, 'DE')"
                      :customService="getDatasetConceptInTransactionContext" :onlyActiveAssociations="true">
                      <template #inline.header="{ object }">
                        <div class="scenario-details-card-header">
                          <AdCardTitle key="title">
                            <AdStatusDot darker larger :status="object.statusCode" :icon="$filters.getDatasetIcon(item)"
                              class="mr-2 mb-1" />
                            {{ $filters.getMatchingTranslation({ value: object.name }) }}
                          </AdCardTitle>
                          <v-card-subtitle>
                            <span class="pl-8" v-if="item && !item.absent">
                              {{ cardinality(item) }}
                              {{ conformance(item) }}
                            </span>
                            <span v-else>
                              {{ $t('not-member-of-transaction') }}
                            </span>
                          </v-card-subtitle>
                        </div>
                      </template>

                      <template #inline.content.meta="{ details }">
                        <EditableArtefactDetailHeader :item="details" :locales="projectLocales"
                          artefact="DatasetConcept" readonly :showMore.sync="datasetMetaDataIsExpanded">
                          <DatasetMetaInformation :value="details" readonly />
                        </EditableArtefactDetailHeader>
                      </template>

                      <template #inline.content.append="{ details }">
                        <!-- show inheritance and containment -->
                        <v-row dense v-if="details.inherit" class="align-center pb-2">
                          <v-col class="flex-grow-0 text-no-wrap mr-3">
                            <span>{{ $t('inherited-from') }}</span>
                          </v-col>
                          <v-col class="flex-grow-1">
                            <AdArtefactDetailsCard :object="details.inherit[0]" />
                          </v-col>
                        </v-row>
                        <v-row dense v-if="details.contains" class="align-center pb-2">
                          <v-col class="flex-grow-0 text-no-wrap mr-3">
                            <span>{{ $t('reference') }}</span>
                          </v-col>
                          <v-col class="flex-grow-1">
                            <AdArtefactDetailsCard :object="details.contains[0]" />
                          </v-col>
                        </v-row>
                        <!-- Show context -->
                        <v-card class="mb-5" v-if="!!details.context">
                          <v-card-text>
                            <EditableTranslationEditor :value="details.context" :locales="projectLocales" readonly
                              :label="$t('context')" editor textarea dense :minHeight="0" />
                          </v-card-text>
                        </v-card>

                        <!-- Show terminology associations -->
                        <TerminologyAssociationCard v-if="terminologyAssociations(details).length"
                          :associations="terminologyAssociations(details)" />

                        <!-- Show identifier associations -->
                        <IdentifierAssociationCard v-if="identifierAssociations(details).length"
                          :associations="identifierAssociations(details)" />

                        <v-row v-if="details.valueDomain" dense class="mt-2">
                          <v-expansion-panels v-model="conceptDetailsValueDomainPanel" dense class="v-sheet--outlined"
                            :disabled="!valueDomainConceptListConcepts(details).length && !valuesetAssociations(details).length">
                            <v-expansion-panel>
                              <v-expansion-panel-header>
                                <AdCardTitle inline style="padding: 0 !important">
                                  {{ $t('value') }} &ndash;
                                  <v-icon class="pl-1" style="justify-content: left">
                                    {{ $filters.getValueDomainTypeIcon(valueDomainType(details)) }}
                                  </v-icon>
                                  <span class="value-domain-type-label ml-1">{{
                                    valueDomainLabel(valueDomainType(details)) }}</span>
                                </AdCardTitle>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content
                                v-if="valueDomainConceptListConcepts(details).length || valuesetAssociations(details).length">

                                <!-- Show value set associations to conceptLists -->
                                <ValuesetAssociationCard v-if="valuesetAssociations(details).length"
                                  :associations="valuesetAssociations(details)" :showSelect="false" />

                                <!-- Show conceptList choices (choice list) -->
                                <div v-if="valueDomainConceptListConcepts(details).length > 0" class="py-5">
                                  <span>{{ $t('choice-list') }}: </span>
                                  <v-chip class="ml-0 mr-1 mb-1 black--text"
                                    v-for="(g, key) in valueDomainConceptListConcepts(details)" :key="`vdclc${key}`"
                                    small>
                                    {{ $filters.getMatchingTranslation({ value: g.name }) }}
                                  </v-chip>
                                </div>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>

                        </v-row>
                        <UsageSection
                          v-if="details.id && details.effectiveDate"
                          :key="`usage-section-${details.id}-${details.effectiveDate}`"
                          :expanded.sync="usageSectionIsExpanded"
                          :objectId="details.id"
                          :objectEffectiveDate="details.effectiveDate"
                          action="dataset/getConceptUsage"
                        />
                        <CommunitySection :item="details" />
                      </template>
                    </AdArtefactDetailsCard>

                    <AdConditional :conditions="item.condition" :enableWhen="item.enableWhen"
                      :getQuestionName="getQuestionName">
                      <template #title>
                        <AdCardTitle inline style="padding: 0 !important">
                          <span>{{ $t('conditions') }}</span>
                          <v-tooltip v-if="item.enableBehavior" top>
                            <template #activator="{on}">
                              <v-chip v-on="on" class="ml-2" small
                                :color="item.enableBehavior === 'all' ? 'orange lighten-5' : 'green lighten-4'">{{
                                item.enableBehavior }}</v-chip>
                            </template>
                            {{ $t('enable-behavior') }}: {{ $t(item.enableBehavior) }}
                          </v-tooltip>
                        </AdCardTitle>
                      </template>
                    </AdConditional>

                  </div>
                </template>

                <template v-slot:[`item.custom_name`]="{ item }">
                  <!-- item.contains ? 'mdi-movie-filter-outline' : $filters.getDatasetIcon(item) -->
                  <v-icon :color="$filters.getStatusColor(item.statusCode)">
                    {{ $filters.getDatasetIcon(item) }}
                  </v-icon>
                  <!-- dataset: mdi-book-open-blank-variant, scenario: mdi-movie mdi-swap-horizontal mdi-movie-open-outline -->
                  <v-icon v-if="item.contains2" color="grey lighten-1" class="pl-1">
                    mdi-book-open-blank-variant
                  </v-icon>
                  <span :class="item.absent ? 'grey--text' : 'font-weight-bold'" class="pl-2">
                    {{ $filters.getMatchingTranslation({ value: item.name }) }}
                    <span class="grey--text" v-if="item && !item.absent && !item.transaction">
                      –
                      {{ cardinality(item) }}
                      {{ conformance(item) }}
                    </span>
                  </span>
                  <span v-if="item.transaction" class="pl-2">
                    {{ $t('as-in') }}
                    <v-icon color="grey lighten-1" class="pl-1">
                      mdi-movie
                    </v-icon>
                    <span class="pl-2">Patient (typical)</span>
                  </span>
                </template>

                <template v-slot:[`item.name`]="{ item }">
                  <v-icon class="pr-2" :color="$filters.getStatusColor(item.statusCode)">
                    {{ $filters.getDatasetIcon(item) }}
                  </v-icon>
                  <span :class="item.absent ? 'grey-text' : 'font-weight-bold'">
                    {{ $filters.getMatchingTranslation({ value: item.name }) }}
                  </span>
                </template>

                <template v-slot:[`item.selection`]="{ item }">
                  <ScenarioSelectionSpeedDialButton :item="item" :edit-mode="editMode" @setCardConf="setCardConfHandler"
                    @unselectItem="unselectItem" />
                </template>

                <template #item.card="{item}">
                  <EditableCardinality
                    v-if="!item.absent && item.conformance !== 'NP'"
                    :value="[item.minimumMultiplicity, '..', item.maximumMultiplicity].join('')"
                    style="width: 90px"
                    :conformance="item.conformance"
                    :items="getPossibleCardinalities(item.conformance).map(_ => ({ value: _, text: _ }))"
                    :readonly="!editMode"
                    @input="updateCardinality(item, $event)"
                    dense
                  />
                </template>

                <template v-slot:[`item.conformance`]="{ item }">
                  <EditableDropdown v-if="!item.absent" dense v-model="item.conformance" :items="confItems"
                    style="width: 90px" :readonly="!editMode" @change="updateConformance(item, $event)" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="d-flex justify-end">
                    <AdSpeedDial direction="left">
                      <AdButton v-if="item.conformance === 'C'" icon="mdi-forwardburger" :hint="$t('condition')" list
                        @click="startEditConditions(item)" noMarginRight />
                      <AdButton icon="mdi-gamepad-circle-right" :hint="$t('context')" list
                        @click="startEditContext(item)" noMarginRight />
                    </AdSpeedDial>
                  </div>
                </template>
              </TreeTable>
            </v-card-text>
          </v-card>
        </template>

        <HistorySection
          v-if="editableItem.id && editableItem.effectiveDate"
          :expanded.sync="historySectionIsExpanded"
          :objectId="editableItem.id" :objectEffectiveDate="editableItem.effectiveDate"
          :artefact="(isTransactionGroup || isTransactionLeaf) ? 'TR' : 'SC'"
        />

        <IssueAssociationsSection
          v-if="editableItem.id && editableItem.effectiveDate"
          ref="issueSection"
          :count="null"
          :objectId="editableItem.id"
          :objectEffectiveDate="editableItem.effectiveDate"
        />
      </v-card-text>
    </v-card>

    <ScenarioParentCardinalityDialog ref="parentCardinalityDialog" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { xorWith, isEqual, cloneDeep } from 'lodash'
import AdArtefactDownloads from '@/components/Rule/AdArtefactDownloads'
import ScenarioCompareDialog from '@/components/Scenario/ScenarioCompareDialog'
import TreeTable from '@/components/Utilities/TreeTable/TreeTable'
import ScenarioService from '@/services/ScenarioService'
import DataSetService from '@/services/DataSetService'
import ScenarioSelectionSpeedDialButton from '@/components/Scenario/ScenarioSelectionSpeedDialButton'
import AdCreateSvg from '@/components/Utilities/AdCreateSvg'
import IssueCreationModal from '@/components/Issue/IssueCreationModal'
import IssueAssociationsSection from '@/components/Utilities/IssueAssociationsSection'
import AdConditional from '@/components/Utilities/AdConditional'
import AdArtefactSelection from '@/components/Utilities/AdArtefactSelection'
import EditableRichText from '@/components/Utilities/Editable/EditableRichText'
import ScenarioEditConditionsDialog from '@/components/Scenario/ScenarioEditConditionsDialog'
import ScenarioActorDialog from '@/components/Scenario/ScenarioActorDialog'
import EditableArtefactDetailHeader from '@/components/Utilities/Editable/EditableStructure/EditableArtefactDetailHeader'
import LockService from '@/services/LockService'
import ConformanceCardinalityHelper from '@/components/Scenario/ConformanceCardinalityHelper'
import HistorySection from '@/components/Utilities/HistorySection'
import DatasetMetaInformation from '@/components/Dataset/DatasetMetaInformation'
import ScenarioSimplifiedSpreadsheetOrGraphDialog from '@/components/Scenario/ScenarioSimplifiedSpreadsheetOrGraphDialog'
import CommunitySection from '@/components/Utilities/CommunitySection'
import ScenarioParentCardinalityDialog from './ScenarioParentCardinalityDialog.vue'
import SmartCloneDialog from './SmartCloneDialog.vue'
import IdentifierAssociationCard from '@/components/Dataset/IdentifierAssociationCard'
import ValuesetAssociationCard from '@/components/Dataset/ValuesetAssociationCard'
import TerminologyAssociationCard from '@/components/Dataset/TerminologyAssociationCard'
import AdCopyrightCrud from '@/components/Utilities/AdCopyrightCrud'
import UsageSection from '@/components/Utilities/UsageSection'

export default {
  name: 'ScenarioDetailsCard',
  components: {
    AdArtefactDownloads,
    ScenarioParentCardinalityDialog,
    SmartCloneDialog,
    CommunitySection,
    ScenarioActorDialog,
    ScenarioEditConditionsDialog,
    IssueAssociationsSection,
    IssueCreationModal,
    AdCreateSvg,
    ScenarioSelectionSpeedDialButton,
    TreeTable,
    ScenarioCompareDialog,
    EditableArtefactDetailHeader,
    EditableRichText,
    AdConditional,
    AdArtefactSelection,
    HistorySection,
    DatasetMetaInformation,
    ScenarioSimplifiedSpreadsheetOrGraphDialog,
    TerminologyAssociationCard,
    ValuesetAssociationCard,
    IdentifierAssociationCard,
    AdCopyrightCrud,
    UsageSection
  },

  mixins: [
    ConformanceCardinalityHelper
  ],

  data () {
    return {
      edit: false,
      editableItem: {},
      originalItem: {},
      rules: {},
      editMode: false,
      fullscreen: false,
      fullTreeLoaded: false,

      confItems: ['', 'M', 'R', 'O', 'NP', 'C'],

      activeConceptItem: null,
      currentScenarioId: null,
      representingTemplate: null,
      itemsRepresentingTemplate: [],
      itemsRepresentingTemplateBeforeEdit: [],
      loading: {
        representingTemplate: false,
        sourceDataset: false,
        representingQuestionnaire: false,
        copyrightCrud: false
      },
      modals: {
        conditions: {
          open: false,
          selected: null
        },
        context: {
          open: false,
          selected: null
        },
        artefactSelection: {
          open: false,
          selected: null,
          tree: null,
          selectHandler: null,
          filters: []
        },
        artefactDownload: false,
        smartCloneDialog: {
          open: false,
          transaction: null,
          newDataset: null
        },
        spreadsheetOrGraph: {
          graph: false,
          spreadsheet: false
        }
      },
      conceptDetailsValueDomainPanel: 0,
      editingCopyrightCrud: false
    }
  },

  computed: {
    ...mapState({
      isLoadingTransactionItem: (state) => state.scenario.loading.transactionItem,
      transactionItemConcepts: (state) => state.scenario.transactionItemConcepts || [],
      transactionItem: (state) => state.scenario.transactionItem,
      scenarioItems: (state) => state.scenario.scenarioItems || [],
      scenarioItem: (state) => state.scenario.scenarioItem
    }),

    ...mapGetters('server', ['getItemStatuses', 'getTemplateStatuses']),

    abortController: {
      get () {
        return this.$store.getters['scenario/abortController']
      },
      set (value) {
        this.$store.commit('scenario/setAbortController', value)
      }
    },

    actorList () {
      return this.$store.getters['scenario/actors']
    },

    artefactSelectionAddButtonType () {
      return this.showSmartCloneSourceDatasetOptions
        ? 'reassign'
        : 'save'
    },
    isEditingSourceDataset () {
      return this.modals.artefactSelection.tree?.artefact === 'DS'
    },
    showSmartCloneSourceDatasetOptions () {
      return this.isEditingSourceDataset && this.modals.artefactSelection.selected?.length
    },

    transactionLeafTypes () {
      return this.$store.getters['server/getTransactionTypes']
        .filter((type) => type.value !== 'group')
    },

    usageSectionIsExpanded: {
      get () {
        return this.$store.state.dataset.expandedSections.usageSection
      },
      set (value) {
        this.$store.commit('dataset/setExpandedSection', { section: 'usageSection', value })
      }
    },

    /**
     * Get actor types.
     * @returns {any}
     */
    actorTypes () {
      return this.$store.getters['server/getActorTypes']
    },

    /**
     * Get actor headers.
     * @returns {[]}
     */
    actorHeaders () {
      return [
        { text: this.$t('type'), value: 'type', sortable: true, width: '100px' },
        { text: this.$t('role'), value: 'role', sortable: true, width: '5%' },
        { text: this.$t('name'), value: 'name', sortable: false, width: '25%' },
        { text: this.$t('description'), value: 'desc', sortable: false, width: '65%' },
        {
          text: this.isEditable ? this.$t('actions') : '',
          value: 'data-actions',
          width: '100px',
          hidden: false,
          sortable: false
        }
      ]
    },

    /**
     * Get representing template headers for read mode.
     * @returns {[]}
     */
    headersRepresentingTemplateRead () {
      return [
        { text: this.$t('name'), value: 'custom_name', width: 'auto' }
      ]
    },

    /**
     * Get representing template headers for edit mode.
     * @returns {[]}
     */
    headersRepresentingTemplateEdit () {
      return [
        { text: this.$t('name'), value: 'name', width: '400px' },
        { text: this.$t('selection'), value: 'selection', width: '70px' },
        { text: this.$t('card'), value: 'card', width: '70px' },
        { text: this.$t('conf'), value: 'conformance', width: '70px' },
        { text: this.$t('actions'), value: 'actions', align: 'right' }
      ]
    },
    datasetMetaDataIsExpanded: {
      get () {
        return this.$store.state.dataset.expandedSections.metadata
      },
      set (value) {
        this.$store.commit('dataset/setExpandedSection', { section: 'metadata', value })
      }
    },
    historySectionIsExpanded: {
      get () {
        return this.$store.state.scenario.expandedSections.historySection
      },
      set (value) {
        this.$store.commit('scenario/setExpandedSection', { section: 'historySection', value })
      }
    },

    /**
     * Check if scenario item is type of transaction.
     * @returns {boolean}
     */
    isTransactionItem () {
      return this.scenarioItems.find((item) => {
        return item?.id === this.scenarioItem?.id &&
          item?.effectiveDate === this.scenarioItem?.effectiveDate
      }) === undefined
    },

    /**
     * Return true if scenario item is of type initial, back or stationary.
     * @returns {boolean}
     */
    isTransactionLeaf () {
      return (['initial', 'back', 'stationary'].indexOf(this.scenarioItem?.type) >= 0)
    },

    /**
     * Return true if scenario item is of type group.
     * @returns {boolean}
     */
    isTransactionGroup () {
      return (this.scenarioItem?.type === 'group')
    },

    /**
     * Check if scenario item is editable.
     * @returns {boolean}
     */
    isEditable () {
      return (
        this.userIsEditor &&
        ['draft', 'new'].indexOf(this.scenarioItem?.statusCode) >= 0
      )
    },

    isLeafAndHasRepresentingTemplate () {
      return this.isTransactionLeaf && !!this.scenarioItem?.representingTemplate
    },

    /**
     * Get scenario name.
     * @returns {string}
     */
    scenarioName () {
      return this.$filters.getMatchingTranslation({
        value: this.scenarioItem?.name,
        returnObject: true
      })?.['#text']
    },

    /**
     * Get representing template info.
     * @returns {{}}
     */
    representingTemplateInfo () {
      return {
        type: 'TM',
        id: this.scenarioItem?.representingTemplate?.[0]?.templateId,
        effectiveDate: this.scenarioItem?.representingTemplate?.[0]?.templateEffectiveDate,
        displayName: this.scenarioItem?.representingTemplate?.[0]?.templateDisplayName,
        flexibility: this.scenarioItem?.representingTemplate?.[0]?.flexibility,
        statusCode: this.scenarioItem?.representingTemplate?.[0]?.templateStatusCode,
        ident: this.projectInFocus?.prefix
      }
    },

    representingTemplateQuestionnaireInfo () {
      return {
        type: 'QQ',
        id: this.scenarioItem?.representingTemplate?.[0]?.representingQuestionnaire,
        flexibility: this.scenarioItem?.representingTemplate?.[0]?.representingQuestionnaireFlexibility,
        name: this.scenarioItem?.representingTemplate?.[0]?.questionnaireName,
        statusCode: this.scenarioItem?.representingTemplate?.[0]?.questionnaireStatusCode,
        effectiveDate: this.scenarioItem?.representingTemplate?.[0]?.questionnaireEffectiveDate
      }
    },

    scenarioDownloadData () {
      if (!this.scenarioItem?.representingTemplate) return []
      return [
        {
          categoryTitle: this.$t('DECOR'),
          color: 'green',
          links: [
            {
              text: 'XML',
              href: this.getExtractTransactionDownloadLink(this.scenarioItem, 'xml')
            },
            {
              text: 'JSON',
              href: this.getExtractTransactionDownloadLink(this.scenarioItem, 'json')
            }
          ]
        },
        {
          categoryTitle: this.$t('FHIR'),
          color: 'green',
          links: ['1.0', '3.0', '4.0', '4.3', '5.0'].flatMap(format => ([
            {
              text: `${this.$store.getters['server/getFhirLabel'](format)} - XML`,
              condition: () => this.$store.getters['project/projectHasFhirService'](format),
              href: this.getTransactionFhirDownloadLink(this.scenarioItem, format)
            },
            {
              text: `${this.$store.getters['server/getFhirLabel'](format)} - JSON`,
              condition: () => this.$store.getters['project/projectHasFhirService'](format),
              href: this.getTransactionFhirDownloadLink(this.scenarioItem, format, 'json')
            }
          ]))
        }
      ]
    },

    metadataIsExpanded: {
      get () {
        return this.$store.state.scenario.expandedSections.metadata
      },
      set (value) {
        this.$store.commit('scenario/setExpandedSection', { section: 'metadata', value })
      }
    },

    showMoreMetaData: {
      get () {
        return this.metadataIsExpanded || !!this.edit
      },
      set (value) {
        if (!this.edit) {
          this.metadataIsExpanded = value
        }
      }
    },

    /**
     * Get source dataset info.
     * @returns {{}}
     */
    sourceDatasetInfo () {
      return {
        type: 'DS',
        id: this.scenarioItem?.representingTemplate?.[0]?.sourceDataset,
        effectiveDate: this.scenarioItem?.representingTemplate?.[0]?.sourceDatasetEffectiveDate,
        name: this.scenarioItem?.representingTemplate?.[0]?.sourceDatasetName,
        statusCode: this.scenarioItem?.representingTemplate?.[0]?.sourceDatasetStatusCode,
        versionLabel: this.scenarioItem?.representingTemplate?.[0]?.sourceDatasetVersionLabel,
        flexibility: this.scenarioItem.representingTemplate?.[0]?.sourceDatasetFlexibility
          ? this.scenarioItem.representingTemplate?.[0]?.sourceDatasetFlexibility
          : 'dynamic',
        ident: this.projectInFocus?.prefix
      }
    },

    tableHeight () {
      if (!this.fullscreen) return '600px'
      return 'calc(100vh - 200px)'
    },

    tableAutoGrowOffsetHeight () {
      if (!this.fullscreen) return '170px'
      return '220px'
    },

    /**
     * compute the array of authors of current transaction (scenarioItem)
     */
    transactionActorList () {
      const transactionActorList = []
      for (const actorListItem of this.actorList) {
        const actors = this.scenarioItem?.actors?.[0]?.actor ||
          this.scenarioItem?.actors?.[0]?.actors ||
          []

        const matchingActor = actors.find((actor) => {
          return actor.id === actorListItem?.id
        })

        if (matchingActor) {
          transactionActorList.push({ ...matchingActor, ...actorListItem })
        }
      }

      return transactionActorList
    },

    cardconf () {
      return (item) => {
        let result = null
        if (this.cardinality(item) != null) {
          result = ` (${this.cardinality(item)}`
        }
        if (this.conformance(item) != null) {
          result = ((result != null) ? ' / ' : '') + this.conformance(item)
        }
        if (result != null) {
          result += ')'
        }
        return result
      }
    },
    selectedConditions: {
      get () {
        return this.modals.conditions.selected?.condition
      },
      set (value) {
        this.modals.conditions.selected.condition = value
      }
    },
    selectedEnableWhen: {
      get () {
        return this.modals.conditions.selected?.enableWhen
      },
      set (value) {
        this.modals.conditions.selected.enableWhen = value || []
      }
    },
    selectedEnableBehavior: {
      get () {
        return this.modals.conditions.selected?.enableBehavior
      },
      set (value) {
        this.modals.conditions.selected.enableBehavior = value
      }
    },
    selectedContext: {
      get () {
        return this.modals.context.selected?.context
      },
      set (value) {
        this.modals.context.selected.context = value
      }
    },
    getArtefactRefObjectInfo () {
      return (item, type) => ({
        id: item.id,
        ref: item.ref,
        type,
        name: item.name,
        statusCode: item.statusCode,
        flexibility: item.effectiveDate,
        effectiveDate: item.effectiveDate,
        transactionContext: true
      })
    },
    getQuestionName () {
      return id => {
        const item = this.$filters.listHelper.findItemInList({
          items: this.itemsRepresentingTemplate,
          children: 'concept',
          handler: match => match.id === id
        })
        if (Array.isArray(item?.name)) return this.$filters.getMatchingTranslation({ value: item.name })
        return id
      }
    },
    treeDatasets () {
      return {
        artefact: 'DS',
        name: this.$t('datasets'),
        icon: 'mdi-book-open-blank-variant',
        loading: this.$store.state.dataset.loading.datasetItems,
        loaded: this.$store.state.dataset.loaded.datasetItems,
        items: this.$store.state.dataset.datasetItems,
        menu: false,
        insertable: false,
        editable: false,
        expandable: false,
        key: 'uuid',
        text: 'name.selectorName'
      }
    },
    treeTemplates () {
      return {
        artefact: 'TM',
        name: this.$t('templates'),
        icon: 'mdi-check-circle',
        loading: this.$store.getters['rule/template/isLoading']('templates'),
        loaded: this.$store.getters['rule/template/isLoaded']('templates'),
        menu: this.templatesAreEditable,
        editable: false,
        expandable: true,
        autoExpand: true,
        items: this.$store.getters['rule/template/templates']?.map(cat => ({ ...cat, disabled: true })) || [],
        key: 'uuid',
        text: (item) => {
          const post = item.versionLabel ? ` (${item.versionLabel})` : ''
          return (item.displayName || this.$filters.getMatchingTranslation({ value: item.name })) + post
        },
        children: 'template'

      }
    },
    treeQuestionnaires () {
      return {
        artefact: 'QQ',
        name: this.$t('questionnaires'),
        icon: 'mdi-account-question',
        loading: this.$store.getters['questionnaire/isLoading']('questionnaires'),
        loaded: this.$store.getters['questionnaire/isLoading']('questionnaires'),
        menu: this.questionnairesAreEditable,
        editable: false,
        expandable: true,
        autoExpand: true,
        items: this.$store.getters['questionnaire/questionnaires'] || [],
        key: 'uuid',
        text: (item, returnObject) => this.$filters.getMatchingTranslation({
          value: (item?.name || []).map((name) => ({
            ...name,
            '#text': item?.versionLabel
              ? `${name?.['#text']} (${item?.versionLabel})`
              : name?.['#text']
          })),
          selector: '#text',
          returnObject
        }),
        children: 'questionnaireresponse'

      }
    },
    valueDomainLabel () {
      return type => {
        const valueType = this.$store.getters['server/getDecorTypes']?.data?.DataSetValueType?.[0]?.enumeration?.find(item => item.value === type)
        if (!valueType) return
        return this.$filters.getMatchingTranslation({ value: valueType.label })
      }
    },
    watchDataForFilters () {
      if (this.modals.artefactSelection.tree?.artefact === 'TM') {
        return this.getTemplateStatuses
      }
      return this.getItemStatuses
    },
    copyrightChanged () {
      const originalCopyrightData = this.scenarioItem?.copyright?.map(copyright => ({ '#text': copyright['#text'], language: copyright.language })) || []
      const editedCopyrightData = this.editableItem?.copyright?.map(copyright => copyright['#text'] && { '#text': copyright['#text'], language: copyright.language }).filter(value => value) || []
      return !!xorWith(originalCopyrightData, editedCopyrightData, isEqual).length
    },
    publishingAuthorityChanged () {
      const originalPubAuthData = this.scenarioItem?.publishingAuthority?.map(pa => ({ addrLine: pa.addrLine, id: pa.id, name: pa.name })) || []
      const editedPubAuthData = this.editableItem?.publishingAuthority?.map(pa => {
        const result = { addrLine: pa.addrLine, id: pa.id, name: pa.name }
        if (pa.op === 'remove') {
          result.op = 'remove'
        }
        return result
      }) || []
      return !!xorWith(originalPubAuthData, editedPubAuthData, isEqual).length
    }
  },

  watch: {
    scenarioItem: {
      immediate: true,
      deep: true,
      async handler (scenarioItem) {
        // Check if scenario item is defined.
        if (scenarioItem) {
          // Clone current item.
          this.editableItem = this.$filters.clone(scenarioItem)
          // Load some required data.
          await this.$store.commit('scenario/setTransactionItem', null)
          this.itemsRepresentingTemplate = []
          // Check if abort controller is defined.
          if (this.abortController) {
            // Perform abort.
            await this.abortController.abort()
            // Reset abort controller.
            this.abortController = null
          }
          // Load transaction when it is a transaction leaf.
          if (this.isLeafAndHasRepresentingTemplate) {
            // Get new instance of abort controller.

            this.abortController = new AbortController()
            // Get transaction.
            await this.$store.dispatch('scenario/getTransaction', {
              item: this.scenarioItem,
              fulltree: false,
              abortController: this.abortController
            }).catch(() => {
              this.$store.commit('scenario/setTransactionItemConcepts', [])
              this.itemsRepresentingTemplate = []
            })
          }
          // Auto select active concept item from route params.
          this.autoSelectActiveConceptItem()
        }
      }
    },

    activeConceptItem: {
      immediate: true,
      deep: true,
      async handler (activeConceptItem) {
        if (activeConceptItem) {
          // Clone active concept item.
          const clonedActiveConceptItem = this.$filters.clone(activeConceptItem)
          // Load active concept item.
          this.$store.commit('scenario/setActiveConceptItem', clonedActiveConceptItem)
          // Change route.
          this.$router.push({
            name: 'scenarios-details-concepts',
            params: {
              conceptId: clonedActiveConceptItem?.id,
              conceptVersion: clonedActiveConceptItem?.effectiveDate
            }
          }).catch(() => {
            // Do nothing.
          })
          // Auto select active concept item from route params.
          this.autoSelectActiveConceptItem()
        }
      }
    },

    'projectInFocus.prefix': {
      handler (newVal, oldVal) {
        this.sockets.unsubscribe(`PUT/${oldVal}/transaction`)
        this.sockets.unsubscribe(`PATCH/${oldVal}/concept`)
        this.sockets.unsubscribe(`POST/${oldVal}/datasetStatus`)
        this.sockets.unsubscribe(`POST/${oldVal}/conceptStatus`)
      }
    },
    transactionItem: {
      immediate: true,
      deep: true,
      handler (transactionItem) {
        this.representingTemplate = this.$filters.clone(transactionItem)
      }
    },
    transactionItemConcepts: {
      immediate: true,
      deep: true,
      handler (transactionItemConcepts) {
        this.itemsRepresentingTemplate = this.$filters.clone(transactionItemConcepts)
      }
    },

    watchDataForFilters: {
      immediate: true,
      deep: true,
      handler (getItemStatuses) {
        const defaultActive = this.modals.artefactSelection.tree?.artefact === 'TM'
          ? ['active', 'draft', 'pending']
          : ['new', 'draft', 'pending', 'final']

        this.modals.artefactSelection.filters = getItemStatuses?.map(status => {
          const currentlyActive = this.modals.artefactSelection.filters?.find(filter => filter.key === status.value)?.active
          return {
            name: this.$filters.getMatchingTranslation({ value: status.label, locale: this.$i18n.locale }),
            key: status.value,
            active: currentlyActive !== undefined
              ? currentlyActive
              : defaultActive.includes(status.value)
          }
        }) || ['']
        const referenceFilterActive = this.modals.artefactSelection.filters.find(filter => filter.key === undefined)?.active
        this.modals.artefactSelection.filters.push({
          name: this.$t('none'),
          key: undefined,
          active: referenceFilterActive !== undefined
            ? referenceFilterActive
            : defaultActive.includes('undefined')
        })
      }
    }
  },

  methods: {
    autoSelectActiveConceptItem () {
      // Get route params.
      const { conceptId, conceptVersion } = this.$route.params
      // Check if active concept item is still null but concept id and version from route params are defined.
      if (!this.activeConceptItem && this.itemsRepresentingTemplate.length && conceptId && conceptVersion) {
        // Set active concept item if it could be found.
        this.activeConceptItem = this.getMatchingDatasetConcept(conceptId, conceptVersion)
      }
    },

    cardinality (item) {
      return this.$filters.getCardinality(item)
    },
    conformance (item) {
      return this.$filters.getConformance(item)
    },

    /**
     * Check if item contains type.
     *
     * @param item
     * @param type
     */
    contains (item, type) {
      // Check if item type is array.
      return Array.isArray(item?.[type]) === true
    },

    /**
     * Add meta item.
     *
     * @param type
     */
    addMetaItem (type) {
      this.$set(this.editableItem, type, [])
    },

    getTransactionFhirDownloadLink (item, fhirFormat, dataFormat = 'xml') {
      const BASE_URL = this.$store.getters['project/getFhirURL'](fhirFormat)
      const { id, effectiveDate, ident } = item
      const prefix = this.projectInFocus?.prefix
      const parsedEffectiveDate = effectiveDate.replaceAll(/[-:T]/g, '')
      const url = `${BASE_URL}/${fhirFormat}/${ident || prefix}/StructureDefinition/${id}--${parsedEffectiveDate}?_format=${dataFormat}`
      return url
    },
    getExtractTransactionDownloadLink (item, format) {
      const { id, effectiveDate } = item
      const projectLanguage = this.$store.getters['app/projectLanguage']
      const url = `${this.$store.getters['server/URL_ART_DECOR_API']}/transaction/${id}/${encodeURIComponent(effectiveDate)}/$extract?language=${projectLanguage}&format=${format}&download=true`
      return url
    },

    /**
     * Check if item is visible.
     * If in read mode item should not be absent.
     * @param item
     * @returns {boolean}
     */
    isItemVisible (item) {
      return this.editMode === false
        ? item?.absent !== 'true'
        : true
    },

    /**
     * Get matching dataset concept from representing template.
     * @param id
     * @param effectiveDate
     * @returns {Promise<{}>}
     */
    getMatchingDatasetConcept (id, effectiveDate) {
      return this.$filters.listHelper.findItemInList({
        items: this.itemsRepresentingTemplate,
        children: 'concept',
        handler: (item) => item.id === id && item.effectiveDate === effectiveDate
      })
    },

    async getDatasetConceptInTransactionContext (id, effectiveDate, abortController) {
      const associations = true
      const transactionId = this.scenarioItem?.id
      const transactionEffectiveDate = this.scenarioItem?.effectiveDate
      const matchingConcept = this.getMatchingDatasetConcept(id, effectiveDate)
      const isVisible = matchingConcept
        ? this.isItemVisible(matchingConcept)
        : false

      if (!transactionId || !transactionEffectiveDate || !isVisible) {
        return
      }

      const response = await DataSetService.getDatasetConcept(id, effectiveDate, associations, {
        transactionId,
        transactionEffectiveDate
      }, abortController)
      return response.data
    },

    /**
     * Update cardinality.
     * @param item
     * @param value
     */
    updateCardinality (item, value) {
      // Get cardinality.
      const cardinality = value
        ? value.split('..')
        : null

      item.minimumMultiplicity = cardinality?.[0] || 0
      item.maximumMultiplicity = cardinality?.[1] || 0
      if (item.conformance === 'O') item.minimumMultiplicity = 0
    },

    /**
     * Update conformance.
     * @param item
     * @param value
     */
    updateConformance (item, value) {
      // Update cardinality.
      const defaultCardinality = this.getDefaultCardinality(item.conformance, item.cardinality)
      this.updateCardinality(item, defaultCardinality)

      item.isMandatory = value === 'M' ? 'true' : 'false'

      // Check if conformance is not conditional.
      if (value !== 'C') {
        // Reset conditions.
        item.condition = null
      } else {
        document.activeElement.blur()
        this.startEditConditions(item)
      }
    },

    /**
     * Set representing template info.
     * @returns {{}}
     */
    saveRepresentingTemplateInfo (value) {
      // Define data.
      const data = {
        parameter: []
      }

      // Check if value is not an array.
      if (Array.isArray(value) === false) {
        // Check if id and effective date are available.
        if (value?.id && value?.effectiveDate) {
          // Push replace parameter.
          data.parameter.push({
            op: 'replace',
            path: '/representingTemplate/template',
            value: {
              representingTemplate: {
                ref: value?.id,
                flexibility: value?.effectiveDate
              }
            }
          })
        } else {
          // Push remove parameter.
          data.parameter.push({
            op: 'remove',
            path: '/representingTemplate/template'
          })
        }
      }

      this.loading.representingTemplate = true
      this.$store.dispatch('scenario/patchTransactionItem', {
        item: this.scenarioItem,
        data
      }).catch((error) => {
        // Get error message.
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        // Show error message on confirm dialog.
        this.$oops(errorMessage)
      })
        .finally(() => {
          this.loading.representingTemplate = false
        })
    },

    async saveRepresentingTemplateQuestionnaireInfo (value) {
      const data = { parameter: [] }

      if (value?.id && value?.effectiveDate) {
        // Push replace parameter.
        data.parameter.push({
          op: 'replace',
          path: '/representingTemplate/questionnaire',
          value: {
            representingTemplate: {
              representingQuestionnaire: value?.id,
              representingQuestionnaireFlexibility: value?.effectiveDate
            }
          }
        })
      } else {
        // Push remove parameter.
        data.parameter.push({
          op: 'remove',
          path: '/representingTemplate/questionnaire'
        })
      }

      this.loading.representingQuestionnaire = true
      try {
        await this.$store.dispatch('scenario/patchTransactionItem', {
          item: this.scenarioItem,
          data
        })
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }

      this.loading.representingQuestionnaire = false
    },

    /**
     * Set source dataset info.
     * @returns {{}}
     */
    saveSourceDatasetInfo (value) {
      // Define data.
      const data = {
        parameter: []
      }

      // Check if value is not an array.
      if (Array.isArray(value) === false) {
        // Check if id and effective date are available.
        if (value?.id && value?.effectiveDate) {
          // Push replace parameter.
          data.parameter.push({
            op: 'replace',
            path: '/representingTemplate/dataset',
            value: {
              representingTemplate: {
                sourceDataset: value?.id,
                sourceDatasetFlexibility: value?.effectiveDate
              }
            }
          })
        } else {
          // Push remove parameter.
          data.parameter.push({
            op: 'remove',
            path: '/representingTemplate/dataset'
          })
        }
      }
      this.loading.sourceDataset = true
      this.$store.dispatch('scenario/patchTransactionItem', {
        item: this.scenarioItem,
        data
      }).catch((error) => {
        // Get error message.
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        // Show error message on confirm dialog.
        this.$oops(errorMessage)
      })
        .finally(() => {
          this.fullTreeLoaded = false
          this.loading.sourceDataset = false
        })
    },

    /**
     * Save actor list.
     */
    saveActorList ({ mode, options }) {
      // Define data.
      const data = {
        parameter: []
      }

      // Get actor list.
      const actorList = this.$filters.clone(
        this.scenarioItem?.actors?.[0]?.actor ||
        this.scenarioItem?.actors?.[0]?.actors ||
        []
      )

      // Check if mode is add.
      if (mode === 'add') {
        actorList.push({
          id: options.id,
          role: options.role
        })
      }

      // Check if mode is edit.
      if (mode === 'edit') {
        const matchingActor = actorList.find((actor) => actor.id === options.id)
        const index = actorList.indexOf(matchingActor)
        if (index >= 0) {
          actorList.splice(index, 1, { ...matchingActor, role: options.role })
        }
      }

      // Check if mode is delete.
      if (mode === 'delete') {
        const matchingActor = actorList.find((actor) => actor.id === options.id)
        const index = actorList.indexOf(matchingActor)
        if (index >= 0) {
          actorList.splice(index, 1)
        }
      }

      // Get unique actor list.
      const uniqueActorList = actorList.filter((value, index, self) => {
        const matchingActor = self.find((actor) => actor?.id === value?.id)
        return self.indexOf(matchingActor) === index
      })

      // Push parameter.
      data.parameter.push({
        op: 'replace',
        path: '/actors',
        value: {
          actors: {
            actor: uniqueActorList
          }
        }
      })

      this.$store.dispatch('scenario/patchTransactionItem', {
        item: this.scenarioItem,
        data
      }).catch((error) => {
        // Get error message.
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        // Show error message on confirm dialog.
        this.$oops(errorMessage)
      })
    },

    /**
     * Save meta information value.
     *
     * @param type
     * @param value
     */
    async saveMetaInformationValue (type, value) {
      // Define data.
      const data = {
        parameter: []
      }

      // Check if value is not an array.
      if (Array.isArray(value) === false) {
        // Push parameter.
        data.parameter.push({
          op: !value ? 'remove' : 'replace',
          path: `/${type}`,
          value
        })
      }

      // Check if value is an array.
      if (Array.isArray(value) === true) {
        // Push parameter.
        data.parameter.push(
          ...Object.values(value)
            .filter((currentValue) => currentValue?.['#text'] && currentValue?.language)
            .map((currentValue) => {
              // Check if text is empty.
              if (
                /<p>[ ]{0,}<\/p>/.test(currentValue?.['#text']) ||
                currentValue?.['#text'] === '' ||
                !currentValue?.['#text']) {
                // Get original value.
                const originalValue = (this.scenarioItem?.[type] || []).find(
                  (value) => value.language === currentValue.language
                )

                // Perform remove.
                return {
                  op: 'remove',
                  path: `/${type}`,
                  value: { [type]: originalValue }
                }
              }

              // Perform update or add.
              return {
                op: (this.scenarioItem?.[type] || []).find(
                  (value) => value.language === currentValue.language
                ) !== undefined
                  ? 'replace'
                  : 'add',
                path: `/${type}`,
                value: { [type]: currentValue }
              }
            })
        )
      }

      // Call the patch item method.
      this.patchItem(this.scenarioItem, data)
    },

    startEditConditions (item) {
      this.modals.conditions.selected = item
      this.modals.conditions.open = true
    },
    async startSmartClonePreflight (newDataset) {
      this.modals.smartCloneDialog.transaction = this.scenarioItem
      this.modals.smartCloneDialog.newDataset = newDataset
      this.modals.smartCloneDialog.open = true
    },
    async handleSmartClone (newRepresentingTemplate) {
      // Close the dialog to select a source dataset
      this.modals.artefactSelection.open = false
      this.resetArtefactSelectionModal()

      try {
        if (!this.scenarioItem) throw new Error('scenarioItem not found')

        // Patch the representing template
        const data = {
          parameter: [
            {
              op: 'replace',
              path: '/representingTemplate',
              value: {
                representingTemplate: newRepresentingTemplate
              }
            }
          ]
        }
        const res = await ScenarioService.patchRepresentingTemplate({
          id: this.scenarioItem.id,
          effectiveDate: this.scenarioItem.effectiveDate,
          data
        })

        // Do the same with the response as for a regular patch of a transaction (vuex action patchTransactionItem)
        const patchedItem = { ...this.scenarioItem, ...res.data }
        for (const key of Object.keys(patchedItem)) {
          if (!(key in res.data)) delete patchedItem[key]
        }
        this.$store.commit('scenario/updateScenarioItem', { item: this.scenarioItem, data: patchedItem })
        this.$store.commit('scenario/setScenarioItem', patchedItem)
        this.$store.commit('scenario/setActiveItem', patchedItem)

        // Note: the new representing template itself is automatically fetched when scenatioItem is updated (see watch handler)

        // Make sure the full tree is reloaded when user is going to edit the representing template
        this.fullTreeLoaded = false
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
    },
    resetArtefactSelectionModal () {
      this.modals.artefactSelection.tree = null
      this.modals.artefactSelection.selected = null
      this.modals.artefactSelection.versionProp = null
      this.modals.artefactSelection.selectHandler = null
      this.modals.artefactSelection.filters = []
    },
    async startSelectRepresentingTemplate () {
      if (!this.$store.state.rule.template.loaded.templates) {
        try {
          await this.$store.dispatch('rule/template/setTemplateList')
        } catch (error) {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        }
      }
      this.modals.artefactSelection.tree = this.treeTemplates
      this.modals.artefactSelection.selected = this.representingTemplateInfo.id ? [this.representingTemplateInfo] : []
      this.modals.artefactSelection.versionProp = 'version'
      this.modals.artefactSelection.open = true
      this.modals.artefactSelection.selectHandler = this.saveRepresentingTemplateInfo
    },
    async startSelectRepresentingQuestionnaire () {
      if (!this.$store.state.questionnaire.loaded.questionnaires) {
        try {
          await this.$store.dispatch('questionnaire/getQuestionnaireList')
        } catch (error) {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        }
      }
      this.modals.artefactSelection.tree = this.treeQuestionnaires
      this.modals.artefactSelection.selected = this.representingTemplateQuestionnaireInfo.id ? [this.representingTemplateQuestionnaireInfo] : []
      this.modals.artefactSelection.versionProp = null
      this.modals.artefactSelection.open = true
      this.modals.artefactSelection.selectHandler = this.saveRepresentingTemplateQuestionnaireInfo
    },
    async startSelectSourceDataset () {
      if (!this.$store.state.dataset.loaded.datasetItems) {
        try {
          await this.$store.dispatch('dataset/loadDatasetItems')
        } catch (error) {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        }
      }
      this.modals.artefactSelection.tree = this.treeDatasets
      this.modals.artefactSelection.selected = this.sourceDatasetInfo.id ? [this.sourceDatasetInfo] : []
      this.modals.artefactSelection.selectHandler = this.saveSourceDatasetInfo
      this.modals.artefactSelection.open = true
    },
    stopEditConditions () {
      this.modals.conditions.open = false
      this.modals.conditions.selected = null
    },
    startEditContext (item) {
      this.modals.context.selected = item
      this.modals.context.open = true
    },
    stopEditContext () {
      this.modals.context.open = false
      this.modals.context.selected = null
    },
    startSimplifiedSpreadsheet (item) {
      this.modals.spreadsheetOrGraph.open = true
      this.modals.spreadsheetOrGraph.spreadsheet = true
      this.modals.spreadsheetOrGraph.graph = false
    },
    startSimplifiedGraph (item) {
      this.modals.spreadsheetOrGraph.open = true
      this.modals.spreadsheetOrGraph.graph = true
      this.modals.spreadsheetOrGraph.spreadsheet = false
    },
    stopSimplifiedSpreadsheetOrGraph () {
      this.modals.spreadsheetOrGraph.open = false
      this.modals.spreadsheetOrGraph.graph = false
      this.modals.spreadsheetOrGraph.spreadsheet = false
    },

    terminologyAssociations (concept) {
      const list = concept?.terminologyAssociation || []
      return list.filter(item => item.active == null || item.active === 'true')
    },
    identifierAssociations (concept) {
      const list = concept?.identifierAssociation || []
      return list.filter(item => item.active == null || item.active === 'true')
    },
    valuesetAssociations (concept) {
      const list = concept?.valueDomain?.[0].conceptList?.[0].terminologyAssociation || []
      return list.filter(item => item.active == null || item.active === 'true'
      )
    },

    valueDomainType (concept) {
      return concept?.valueDomain?.[0]?.type || null
    },
    valueDomainConceptListConcepts (concept) {
      const max = 40
      const choiceList = concept?.valueDomain?.[0].conceptList?.[0].concept || []
      if (choiceList.length > max) {
        return [...choiceList.slice(0, max), { name: [{ language: 'en-US', '#text': `... + ${choiceList.length - max} ${this.$t('more')}` }] }]
      }
      return choiceList
    },

    /**
     * Patch item.
     */
    patchItem (item, data) {
      // Get dispatch action.
      const dispatchAction = this.isTransactionItem
        ? 'scenario/patchTransactionItem'
        : 'scenario/patchScenarioItem'

      // Patch dataset.
      return this.$store.dispatch(dispatchAction, { item, data })
        .catch((error) => {
          // Get error message.
          const errorMessage = error.message || error?.response?.data?.description || error?.response?.statusText || 'Error'
          // Show error message on alert dialog.
          this.$oops(errorMessage)
        })
    },

    /**
     * Start edit mode.
     */
    async startEditMode () {
      // Check if current edit mode is false.
      if (this.editMode === false) {
        // Get object id.
        const objectId = this.scenarioItem?.id || null
        // Get effective date.
        const effectiveDate = this.scenarioItem?.effectiveDate || null
        // Check if object id is not null.
        if (objectId !== null) {
          // Try to lock object by id and get response.
          await LockService.lockObjectId({ objectId, effectiveDate })
            .then(async () => {
              // Check if a source dataset has been specified.
              const hasSourceDataset = !!this.scenarioItem?.representingTemplate?.[0]?.sourceDataset
              // Check if full tree is not loaded.
              if (hasSourceDataset && this.fullTreeLoaded === false) {
                // Define payload.
                const payload = { item: this.scenarioItem, fulltree: true, abortController: this.abortController }
                // Load representing template asynchronous.
                this.$store.dispatch('scenario/getTransaction', payload)
                  .then(() => (this.fullTreeLoaded = true))
                  .catch(() => false)
              }

              // Save items representing template.
              this.itemsRepresentingTemplateBeforeEdit = this.$filters.clone(this.itemsRepresentingTemplate)
              // Set edit mode to true.
              this.editMode = true

              if (!hasSourceDataset) {
                this.editMode = false
                return this.$oops(this.$t('please-add-source-dataset'))
              }
              if (!this.fullscreen) {
                this.fullscreen = true
              }
            })
            .catch((error) => {
              // Get error message.
              const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
              // Show error message on alert dialog.
              this.$oops(errorMessage)
            })
        }
      }
    },

    /**
     * Cancel edit mode.
     */
    async cancelEditMode () {
      // Check if edit mode is already true.
      if (this.editMode === true) {
        // Get object id.
        const objectId = this.scenarioItem?.id || null
        const effectiveDate = this.scenarioItem?.effectiveDate || null
        // Check if object id is not null.
        if (objectId !== null) {
          // Unlock object id.
          await LockService.unlockObjectId({ objectId, effectiveDate })
          // Restore items representing template.
          this.itemsRepresentingTemplate = this.$filters.clone(this.itemsRepresentingTemplateBeforeEdit)
          // Notify information.
          this.$root.notify.push({
            on: true,
            type: 'success',
            message: 'Scenario changes cancelled'
          })
        }
      }

      // Set edit mode to false.
      if (this.fullscreen) {
        this.fullscreen = false
      }
      this.editMode = false
    },

    /**
     * Save edited values.
     */
    async saveEditedValues () {
      // Get representing template.
      await ScenarioService.updateRepresentingTemplate(
        this.scenarioItem?.id,
        this.scenarioItem?.effectiveDate,
        { ...this.representingTemplate, concept: this.itemsRepresentingTemplate },
        { broadcast: `PUT/${this.projectInFocus?.prefix}/transaction` }
      ).then((response) => {
        this.representingTemplate = { ...response?.data || {}, concept: null }
        const conceptData = response?.data?.concept || []
        this.itemsRepresentingTemplate = this.prepareItemsRepresentingTemplate(conceptData)

        const matchingConcept = this.getMatchingDatasetConcept(this.activeConceptItem?.id, this.activeConceptItem?.effectiveDate)
        this.activeConceptItem = matchingConcept

        if (matchingConcept?.absent === 'true') {
          // Reset active concept item.
          this.activeConceptItem = null
          this.$store.commit('scenario/setActiveConceptItem', null)
          // Change route.
          this.$router.push({
            name: 'scenarios-details'
          }).catch(() => {
            // Do nothing.
          })
        }

        // Set edit mode to false.
        if (this.fullscreen) {
          this.fullscreen = false
        }
        this.editMode = false
        // Notify information.
        this.$root.notify.push({
          on: true,
          type: 'success',
          message: 'Scenario changes saved'
        })
      }).catch((error) => {
        // Get error message.
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        // Show error message on alert dialog.
        this.$oops(errorMessage)

        // Notify information.
        this.$root.notify.push({
          on: true,
          type: 'warning',
          message: 'Scenario changes are not yet saved'
        })
      })
    },

    /**
     * Set card conf.
     * @param item
     * @param data
     */
    setCardConfHandler ({ item, data }) {
      const match = this.$filters.listHelper.listHelper({
        items: this.itemsRepresentingTemplate,
        children: 'concept',
        handler: (parent) => !!(parent?.concept || []).find((concept) => concept?.id === item?.id)
      })

      if (match && !!match?.item?.absent) {
        this.$refs.parentCardinalityDialog.show(data, (dialogData) => {
          this.setCardConf({ item: match.item, data: dialogData, parent: true })
          this.setCardConf({ item, data })
        })
      } else {
        this.setCardConf({ item, data })
      }
    },

    setCardConf ({ item, data, parent = false }) {
      if (parent === true) {
        const match = this.$filters.listHelper.listHelper({
          items: this.itemsRepresentingTemplate,
          children: 'concept',
          handler: (parent) => !!(parent?.concept || []).find((concept) => concept?.id === item?.id)
        })

        if (match && !!match?.item?.absent) {
          this.setCardConf({ item: match.item, data, parent })
        }
      }

      // Set item values.
      this.$set(item, 'absent', false)
      this.$set(item, 'minimumMultiplicity', data?.itemMin)
      this.$set(item, 'maximumMultiplicity', data?.itemMax)
      this.$set(item, 'conformance', data?.itemConf)

      // Check if type is group and item has concept.
      if (data?.type === 'group' && Array.isArray(item?.concept) === true) {
        // Loop each concept and call set card conf for each item.
        item.concept.forEach((concept) => this.setCardConf({
          item: concept,
          data: {
            type: data?.type,
            itemMin: data?.childMin || data?.itemMin,
            itemMax: data?.childMax || data?.itemMax,
            itemConf: data?.childConf || data?.itemConf
          }
        }))
      }
    },

    /**
     * Reset (unselect) cardinalities and conformance, if needed recursively.
     * @param item
     */
    unselectItem ({ item }) {
      // Set item values.
      this.$set(item, 'absent', true)
      this.$set(item, 'minimumMultiplicity', '')
      this.$set(item, 'maximumMultiplicity', '')
      this.$set(item, 'conformance', '')
      this.$set(item, 'condition', '')

      // Check if type is group and item has concept.
      if (Array.isArray(item?.concept) === true) {
        // Loop each concept and call set card conf for each item.
        item.concept.forEach((concept) => this.unselectItem({
          item: concept
        }))
      }
    },

    /**
     * Get matching actor type label.
     * @param type
     * @returns {*}
     */
    getMatchingActorTypeLabel (type) {
      return this.actorTypes.find((actorType) => actorType.value === type)?.label
    },

    prepareItemsRepresentingTemplate (conceptData) {
      // Check if concept data is an array.
      if (!Array.isArray(conceptData)) {
        return []
      }
      // Map concept data and prepare each item.
      return conceptData.map((item) => {
        // Get minimum multiplicity.
        const minimumMultiplicity = item?.minimumMultiplicity ?? (item?.absent ? '' : '0')
        // Get maximum multiplicity.
        const maximumMultiplicity = item?.maximumMultiplicity ?? (item?.absent ? '' : '*')
        // Get an optionally correct conformance.
        let conformance = item?.conformance

        if (conformance === '') {
          conformance = item?.absent ? '' : minimumMultiplicity === '0' ? 'O' : ''
        }

        if (maximumMultiplicity === '0') {
          conformance = 'NP'
        }

        if (Array.isArray(item?.concept)) {
          item.concept = this.prepareItemsRepresentingTemplate(item.concept)
        }

        // Return prepared item object.
        return {
          ...item,
          minimumMultiplicity,
          maximumMultiplicity,
          conformance
        }
      })
    },
    handleCreateIssue (issue) {
      if (this.$refs?.issueSection?.issueList?.issue) {
        this.$refs.issueSection.issueList.issue.push(issue)
      }
    },
    restoreEditableItem () {
      this.$nextTick(() => {
        this.editableItem = cloneDeep(this.scenarioItem)
      })
    },
    // When the AdCopyrightCrud component is closed with antother selected locale then the projectLocale on the editor, the language needs to be
    // restored to the projectLocale
    restoreCopyrightLocale () {
      this.$refs.copyrightCrud?.restoreLocale()
    },
    handleCancelCopyrightCrud () {
      this.restoreEditableItem()
      this.restoreCopyrightLocale()
      this.editingCopyrightCrud = false
    },
    async handleSaveCopyrightCrud () {
      try {
        const data = { parameter: [] }
        // updates all languages of the copyright
        if (this.copyrightChanged && this.editableItem.copyright) {
          this.editableItem.copyright.forEach((copyright) => {
            if (!this.$filters.htmlHasTextContent(copyright['#text'])) {
              const copyrightToRemove = this.editableItem?.copyright?.find(item => item.language === copyright.language)
              if (copyrightToRemove) {
                data.parameter.push({
                  op: 'remove',
                  path: '/copyright',
                  value: {
                    copyright: copyrightToRemove
                  }
                })
              }
            } else {
              data.parameter.push({
                op: 'replace',
                path: '/copyright',
                value: {
                  copyright
                }
              })
            }
          })
        }
        // Handle changes to publishingAuthority
        if (this.publishingAuthorityChanged && this.editableItem.publishingAuthority) {
          this.editableItem.publishingAuthority.filter((pa) => Object.prototype.hasOwnProperty.call(pa, 'op'))
            .forEach(pa => {
              data.parameter.push({
                op: pa.op,
                path: '/publishingAuthority',
                value: {
                  publishingAuthority: pa
                }
              })
            })
        }
        if (!data.parameter.length) {
          return this.handleCancelCopyrightCrud()
        }
        this.loading.copyrightCrud = true
        await this.patchItem(this.editableItem, data)
        this.$notify(this.$t('submission-update-success-label'), 'success')
        this.editingCopyrightCrud = false
        this.restoreCopyrightLocale()
      } catch (error) {
        this.restoreEditableItem()
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
      this.loading.copyrightCrud = false
    }
  },
  mounted () {
    const prefix = this.projectInFocus?.prefix

    this.sockets.subscribe(`PUT/${prefix}/transaction`, (data) => {
      const { response: representingTemplate, request } = data

      if (request.emitter === this.socketId) {
        return
      }

      const { transactionId, transactionEffectiveDate } = representingTemplate
      const activeItem = this.$store.getters['scenario/activeItem']

      if (activeItem?.id === transactionId && activeItem?.effectiveDate === transactionEffectiveDate) {
        this.representingTemplate = { ...representingTemplate, concept: null }
        const conceptData = representingTemplate.concept
        this.itemsRepresentingTemplate = this.prepareItemsRepresentingTemplate(conceptData)

        this.$notify('The representing template for this transaction has been updated.', 'info', { long: true })
      }
    })

    this.sockets.subscribe(`PATCH/${prefix}/concept`, (data) => {
      const { response: updatedItem, request } = data

      if (request.emitter === this.socketId) {
        return
      }

      // Get reference
      const conceptToUpdate = this.getMatchingDatasetConcept(updatedItem.id, updatedItem.effectiveDate)?.item

      if (!conceptToUpdate) {
        return
      }

      const propsToUpdate = ['name', 'statusCode']

      propsToUpdate.forEach(prop => {
        conceptToUpdate[prop] = updatedItem[prop]
      })
    })

    this.sockets.subscribe(`POST/${prefix}/conceptStatus`, (data) => {
      const { response: updatedItem, request } = data

      if (request.emitter === this.socketId) {
        return
      }

      // Get reference
      const conceptToUpdate = this.getMatchingDatasetConcept(updatedItem.id, updatedItem.effectiveDate)?.item

      if (!conceptToUpdate) {
        return
      }

      const propsToUpdate = ['statusCode']

      propsToUpdate.forEach(prop => {
        conceptToUpdate[prop] = updatedItem[prop]
      })

      if (Array.isArray(conceptToUpdate.concept)) {
        conceptToUpdate.concept.forEach(concept => {
          propsToUpdate.forEach(prop => {
            concept[prop] = updatedItem[prop]
          })
        })
      }
    })

    this.sockets.subscribe(`POST/${prefix}/datasetStatus`, (data) => {
      const { response: report, request } = data

      if (request.emitter === this.socketId) {
        return
      }

      const successfulChanges = report.success
      if (!Array.isArray(successfulChanges)) {
        return
      }

      successfulChanges.forEach(change => {
        const { id, effectiveDate, itemcode, statusCode } = change
        if (itemcode === 'DE') {
          // Get reference
          const conceptToUpdate = this.getMatchingDatasetConcept(id, effectiveDate)?.item

          if (!conceptToUpdate) {
            return
          }

          conceptToUpdate.statusCode = statusCode
        }
      })
    })
  },
  beforeDestroy () {
    const prefix = this.projectInFocus?.prefix
    this.sockets.unsubscribe(`PUT/${prefix}/transaction`)
    this.sockets.unsubscribe(`PATCH/${prefix}/concept`)
    this.sockets.unsubscribe(`POST/${prefix}/datasetStatus`)
    this.sockets.unsubscribe(`POST/${prefix}/conceptStatus`)
  }
}

</script>

<style lang="scss">
.scenario-details-card {
  position: relative;
  top: 0;

  &.editing {
    max-width: 92%;
  }

  &.fullscreen {
    transition: all 1s;
    z-index: 8;
    position: fixed;
    inset: 0;
    margin: 0 !important;
    max-width: 100vw;

    .backdrop-layer,
    .backdrop-layer-helper {
      background-color: white;
    }

    .scenario-details-card-header {
      top: 0;
    }

    .card-backdrop {
      position: relative;
      top: -98px;
    }

    .active-item-layer {
      position: relative;

      > .active-item-inner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        padding: 0 5px 5px 5px;
        margin: 0 -5px;
        overflow: auto;
      }
    }
  }

  .scenario-details-card-header {
    position: sticky;
    top: 48px;
    z-index: 4;
    background: var(--v-topbottomstrip-base);

    ::v-deep .v-card__title {
      background: transparent !important;
    }
  }
}

.tree-table {
  .tree-table-toggle {
    .tree-table-toggle-content {
      align-items: flex-start;

      .tree-table-toggle-content-levels {
        align-items: flex-start;

        .tree-table-toggle-button {
          margin-top: 11px;
        }
      }

      .tree-table-toggle-content-value {
        margin-top: 11px;
      }
    }
  }
}

.backdrop-layer,
.backdrop-layer-helper {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 2;
}

.backdrop-layer-helper {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.22);
}

.backdrop-item {
  position: relative;
  z-index: 3;
}

.tree-table .table-layer .v-data-table {
  position: sticky;
  top: 0px;
}

.valueDomainLabel {
  background-color: #ddd;
  padding: 6px 12px 6px 12px;
  float: left;
  width: 200px;
}

.value-domain-type-label {
  color: rgba(0, 0, 0, 0.54);
}

.scenario-details-card.fullscreen .table-layer .v-data-table .v-data-table__wrapper {
  height: calc(100vh - 200px) !important;
}
</style>
